/** A room's page data: its detail, kept between visits, refreshed while its replay is being made. */
import { useCallback, useEffect, useState } from "react";
import type { InviteSendView, WebinarDetail, WebinarLive } from "../../../shared/types";
import { api } from "../../lib/api";
import { useShownAgain } from "../../lib/inApp";
import { lastLoaded, load, prefetch } from "../../lib/kept";

export type Phase = "loading" | "ready" | "error";

/** Start loading a room's page as a finger lands on its card, so it opens ready. */
export function prefetchRoom(id: string): void {
  prefetch(`webinar:${id}`, () => api.webinars.get(id));
}

export function useRoom(id: string): {
  detail: WebinarDetail | null;
  phase: Phase;
  refresh: () => Promise<void>;
  putLive: (live: WebinarLive) => void;
} {
  const [detail, setDetail] = useState<WebinarDetail | null>(() => lastLoaded(`webinar:${id}`));
  const [phase, setPhase] = useState<Phase>(() => (lastLoaded(`webinar:${id}`) ? "ready" : "loading"));
  const shownAgain = useShownAgain();

  useEffect(() => {
    let alive = true;
    const shown = lastLoaded<WebinarDetail>(`webinar:${id}`);
    setDetail(shown);
    setPhase(shown ? "ready" : "loading");
    load(`webinar:${id}`, () => api.webinars.get(id))
      .then((d) => {
        if (!alive) return;
        setDetail(d);
        setPhase("ready");
      })
      .catch(() => {
        // A page already showing stays when the fresh copy cannot be had.
        if (alive) setPhase(shown ? "ready" : "error");
      });
    return () => {
      alive = false;
    };
  }, [id, shownAgain]);

  const refresh = useCallback(async () => {
    setDetail(await api.webinars.get(id));
  }, [id]);
  // A change to a series answers with the live as the page reads it, so there is nothing to ask again.
  const putLive = useCallback((live: WebinarLive) => setDetail((d) => (d ? { ...d, live } : d)), []);
  return { detail, phase, refresh, putLive };
}

/** The room's invite sends; a send or its reminder replaces its own row. */
export function useInviteSends(id: string): { sends: InviteSendView[]; put: (s: InviteSendView) => void } {
  const [sends, setSends] = useState<InviteSendView[]>([]);
  useEffect(() => {
    let alive = true;
    api.invites
      .list(id)
      .then((r) => alive && setSends(r.sends))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [id]);
  const put = useCallback((s: InviteSendView) => {
    setSends((list) => (list.some((x) => x.id === s.id) ? list.map((x) => (x.id === s.id ? s : x)) : [s, ...list]));
  }, []);
  return { sends, put };
}
